import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { FeatureDto, UpsertTenantRequest } from "../../services/tenantManagementService";

export interface TenantForEdit {
  id: string;
  name: string;
  status: string;
  logoUrl?: string;
}

interface TenantUpsertDialogProps {
  open: boolean;
  mode: "add" | "edit";
  tenant: TenantForEdit | null;
  availableFeatures: FeatureDto[];
  initialFeatureKeys: string[];
  featuresLoading: boolean;
  tenantFeaturesLoading: boolean;
  onClose: () => void;
  onSave: (request: UpsertTenantRequest) => Promise<void>;
}

const toUiStatus = (apiStatus: string): "Active" | "Inactive" =>
  apiStatus === "Suspended" ? "Inactive" : "Active";

const TenantUpsertDialog: React.FC<TenantUpsertDialogProps> = ({
  open,
  mode,
  tenant,
  availableFeatures,
  initialFeatureKeys,
  featuresLoading,
  tenantFeaturesLoading,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState("");
  const [status, setStatus] = useState<"Active" | "Inactive">("Active");
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const featureKeySet = useMemo(() => {
    return new Set(
      (availableFeatures ?? [])
        .map((f) => f.name)
        .filter((k) => typeof k === "string" && k.trim().length > 0)
    );
  }, [availableFeatures]);

  const API_BASE = process.env.REACT_APP_API_URL || "";

  useEffect(() => {
    if (!open) return;

    setError(null);
    setIsSaving(false);

    if (mode === "add") {
      setName("");
      setStatus("Active");
      setSelectedKeys(new Set());
      setLogoFile(null);
      setLogoPreview(null);
      return;
    }

    if (!tenant) return;
    setName(tenant.name ?? "");
    setStatus(toUiStatus(tenant.status));
    setSelectedKeys(new Set(initialFeatureKeys ?? []));
    setLogoFile(null);
 setLogoPreview(tenant.logoUrl ?? null);
  }, [open, mode, tenant, initialFeatureKeys, API_BASE]);

  const toggleFeature = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        return next;
      }
      next.add(key);
      return next;
    });
  };

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Tenant name is required");
      return;
    }

    const filteredKeys = Array.from(selectedKeys)
      .map((k) => k.trim())
      .filter((k) => k.length > 0)
      .filter((k) => featureKeySet.has(k));

    setIsSaving(true);
    setError(null);

    try {
      await onSave({
        name: trimmed,
        description: "",
        status,
        featureKeys: filteredKeys,
        logoFile: logoFile ?? undefined
      });
      onClose();
    } catch (e) {
      const message = e instanceof Error ? e.message : "Failed to save tenant";
      setError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const title = mode === "add" ? "Add Tenant" : `Edit Tenant - ${tenant?.name ?? ""}`;
  const isBusy = featuresLoading || tenantFeaturesLoading;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#111",
          color: "#fff",
          border: "1px solid #333",
        },
      }}
    >
      <DialogTitle sx={{ color: "hsl(19 101% 51% / 1)" }}>{title}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: "rgba(244, 67, 54, 0.1)",
              color: "#fff",
              "& .MuiAlert-icon": { color: "#f44336" },
            }}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField
            label="Tenant Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            InputLabelProps={{ sx: { color: "#ccc" } }}
            InputProps={{ sx: { color: "#fff" } }}
          />

          {/* Tenant Logo Uploader */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ color: "#ccc", mb: 1 }}>Tenant Logo</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <input
                accept="image/*"
                type="file"
                style={{ display: "none" }}
                id="tenant-logo-upload"
                onChange={e => {
                  const file = e.target.files?.[0] || null;
                  setLogoFile(file);
                  setLogoPreview(file ? URL.createObjectURL(file) : null);
                }}
              />
              <label htmlFor="tenant-logo-upload">
                <Button
                  variant="contained"
                  component="span"
                  disabled={isSaving}
                  sx={{
                    backgroundColor: "hsl(19 101% 51% / 1)",
                    color: "#fff",
                    borderColor: "hsl(19 101% 51% / 1)",
                    '&:hover': { backgroundColor: "hsl(19 101% 41% / 1)" },
                    '&:disabled': {
                      backgroundColor: "hsl(19 101% 51% / 0.4)",
                      color: "#fff",
                    },
                  }}
                >
                  Upload Logo
                </Button>
              </label>
              {logoPreview && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  border: "1px solid #333",
                  borderRadius: 1,
                  overflow: "hidden",
                  width: 48,
                  height: 48,
                }}
              >
                <img
                  src={logoPreview}
                  alt="Tenant Logo Preview"
                  style={{
                    width: 48,
                    height: 48,
                    objectFit: "contain",
                    background: "#222",
                  }}
                />
              </Box>

              <IconButton
                size="small"
                onClick={() => {
                  setLogoFile(null);
                  setLogoPreview(null);
                }}
                sx={{
                  color: "#f44336",
                  border: "1px solid #333",
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
            </Box>
            {logoFile && <Typography sx={{ color: "#888", fontSize: 12, mt: 1 }}>{logoFile.name}</Typography>}
          </Box>

          <FormControl fullWidth>
            <InputLabel sx={{ color: "#ccc" }}>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value as "Active" | "Inactive")}
              sx={{ color: "#fff" }}
            >
              <MenuItem value="Active">Active</MenuItem>
              <MenuItem value="Inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <Box>
            <Typography sx={{ color: "#ccc", mb: 1 }}>Features</Typography>
            {isBusy ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress sx={{ color: "hsl(19 101% 51% / 1)" }} />
              </Box>
            ) : (
              <Box
                sx={{
                  maxHeight: 240,
                  overflowY: "auto",
                  border: "1px solid #333",
                  borderRadius: 1,
                  p: 1,
                }}
              >
                {availableFeatures.map((feature) => (
                  <FormControlLabel
                    key={feature.id}
                    control={
                      <Checkbox
                        checked={selectedKeys.has(feature.name)}
                        onChange={() => toggleFeature(feature.name)}
                        sx={{
                          color: "#666",
                          "&.Mui-checked": { color: "hsl(19 101% 51% / 1)" },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ color: "#fff" }}>{feature.name}</Typography>
                        <Typography sx={{ color: "#888", fontSize: 12 }}>
                          {feature.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: "flex-start", m: 0.5 }}
                  />
                ))}
                {availableFeatures.length === 0 && (
                  <Typography sx={{ color: "#888", p: 1 }}>
                    No features available.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: "#fff" }} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving || isBusy}
          sx={{
            backgroundColor: "hsl(19 101% 51% / 1)",
            "&:hover": { backgroundColor: "hsl(19 101% 41% / 1)" },
            "&:disabled": {
              backgroundColor: "hsl(19 101% 51% / 0.4)",
              color: "#fff",
            },
          }}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TenantUpsertDialog;
